import { forwardRef, useState, Children, ReactElement, ReactNode, CSSProperties, MouseEvent, ElementType } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import { i18n } from 'shared/i18n';
import { useUser } from 'app/state/contexts/UserContext';

interface Organization {
  id: number;
  name: string;
}

type NamedMenuChild = ReactElement<{ children: ReactElement<{ 'data-name': string }> }>;

interface CustomToggleProps {
  children?: ReactNode;
  onClick?: (e: MouseEvent) => void;
}

const CustomToggle = forwardRef<HTMLButtonElement, CustomToggleProps>(({ children, onClick }, ref) => (
  <button className="fs-3 fw-medium px-4 bg-transparent h-100 border-0 border-start" ref={ref} onClick={onClick}>
    {children}
  </button>
));

interface CustomMenuProps {
  children: ReactElement[];
  style?: CSSProperties;
  className?: string;
  'aria-labelledby'?: string;
}

const CustomMenu = forwardRef<HTMLDivElement, CustomMenuProps>(({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
  const [value, setValue] = useState('');

  const showFilter = children.length > 5;

  return (
    <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
      {showFilter ? (
        <div className="px-3">
          <Form.Control
            autoFocus
            className="my-2 w-100"
            placeholder={i18n('organization-menu:filter')}
            onChange={(e) => setValue(e.target.value)}
            value={value}
            size="sm"
          />
        </div>
      ) : null}

      <ul className="list-unstyled mb-0">
        {Children.toArray(children).filter(
          (child) => !value || (child as NamedMenuChild).props.children.props['data-name'].toLocaleLowerCase().includes(value.toLocaleLowerCase())
        )}
      </ul>
    </div>
  );
});

export default function OrganizationMenu() {
  const { organizations, selectedOrganization, selectOrganization } = useUser();
  const navigate = useNavigate();
  const orgs = organizations as Organization[] | undefined;

  const handleSelectOrganization = (orgId: number) => {
    navigate({ to: '/' }).then(() => selectOrganization?.(orgId));
  };
  if (!orgs) {
    return null;
  }
  return (
    <Dropdown>
      <Dropdown.Toggle as={CustomToggle as ElementType}>{orgs.find((organization) => organization.id === selectedOrganization)?.name || '???'}</Dropdown.Toggle>
      <Dropdown.Menu className="border-gray-300 rounded-sm min-wr-15" as={CustomMenu as ElementType}>
        {orgs.map((organization) => (
          <Dropdown.Item className="px-3 py-2" onClick={() => handleSelectOrganization(organization.id)} key={organization.id} eventKey={organization.id}>
            {organization.id === selectedOrganization ? (
              <div data-name={organization.name}>
                <span className="text-blue-800">{organization.name}</span>
                <i className="text-blue-800 fa-regular fa-check ms-3" />
              </div>
            ) : (
              <div data-name={organization.name}>{organization.name}</div>
            )}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
